/**
 * DateRowInput (CHP-396)
 *
 * A date input whose ENTIRE row is the click target — clicking anywhere on the
 * row opens the native date picker, not just the small calendar glyph. Extracted
 * from the CHP-309 pattern in DealExtensionRequestForm so every `type="date"`
 * field can share it.
 *
 * The wrapper is purely an enhancement over the native input: the inner
 * `<input type="date">` keeps its own keyboard/tab/focus/Enter behaviour, and a
 * click that lands on the input itself falls through to the browser's default.
 * All standard input props (id, value, onChange, min, disabled, aria-label, …)
 * are forwarded to the inner input, so each call-site keeps its existing wiring.
 */

import * as React from "react";

import { cn } from "@/lib/utils";

export type DateRowInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, "type"> & {
  /** Extra classes for the clickable wrapper row (not the input). */
  wrapperClassName?: string;
};

export const DateRowInput = React.forwardRef<HTMLInputElement, DateRowInputProps>(
  ({ className, wrapperClassName, disabled, ...props }, forwardedRef) => {
    const innerRef = React.useRef<HTMLInputElement>(null);
    // Expose the inner input to a forwarded ref while keeping our own handle.
    React.useImperativeHandle(forwardedRef, () => innerRef.current as HTMLInputElement);

    // Clicking anywhere on the row opens the native picker via showPicker();
    // a click that lands on the input itself is left to the browser.
    const openPicker = (e: React.MouseEvent) => {
      if (disabled) return;
      const input = innerRef.current;
      if (!input) return;
      if (e.target === input) return;
      if (typeof input.showPicker === "function") {
        input.showPicker();
      } else {
        input.focus();
      }
    };

    return (
      <div
        onClick={openPicker}
        className={cn(
          "flex w-full cursor-pointer items-center rounded-xl border border-input bg-card",
          disabled && "cursor-not-allowed opacity-50",
          wrapperClassName,
        )}
      >
        <input
          ref={innerRef}
          type="date"
          disabled={disabled}
          className={cn(
            "flex h-10 w-full cursor-pointer rounded-xl bg-transparent px-3.5 py-2 text-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed",
            className,
          )}
          {...props}
        />
      </div>
    );
  },
);

DateRowInput.displayName = "DateRowInput";
